import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Drawer, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton,
  ButtonBase,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Container,
  Avatar,
  Menu,
  MenuItem,
  ListItemButton,
  Tooltip,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  alpha,
  Fade,
  Grow
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
  Dashboard as DashboardIcon,
  Groups as GroupsIcon,
  Assessment as AssessmentIcon,
  LocalFireDepartment as LocalFireDepartmentIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Logout as LogoutIcon,
  Campaign as CampaignIcon,
  NoteAlt as NoteAltIcon,
  AssignmentTurnedIn as AssignmentIcon,
  LockReset as LockResetIcon,
  Group as GroupIcon,
  MenuOpen as MenuOpenIcon,
  Feedback as FeedbackIcon,
  MenuBook as MenuBookIcon,
  FiberManualRecord as DotIcon,
  Foundation as FoundationIcon,
  Folder as FolderIcon,
  CalendarMonth as CalendarMonthIcon,
  ChildCare as ChildCareIcon,
  Science as ScienceIcon,
  EmojiEvents as EmojiEventsIcon,
  Insights as InsightsIcon
} from '@mui/icons-material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import { useAuth } from '../features/auth/useAuth';
import { filterMenuItems } from './menuFilter';
import { NotificationBell } from '../components/common/notifications/NotificationBell';
import { ClientOmnibox } from '../features/clients/components/ClientOmnibox';

const drawerWidth = 264;

const AppLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, userRole, resetPassword, hasFeature } = useAuth();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordResetEmail, setPasswordResetEmail] = useState('');
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState<string | null>(null);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  // Set drawer to open by default on larger screens
  useEffect(() => {
    if (!isSmallScreen) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isSmallScreen]);

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePasswordResetClick = () => {
    handleMenuClose();
    setPasswordResetEmail(user?.email || '');
    setOpenPasswordDialog(true);
  };

  const handlePasswordDialogClose = () => {
    setOpenPasswordDialog(false);
  };

  const handlePasswordReset = async () => {
    try {
      await resetPassword(passwordResetEmail);
      setPasswordResetSuccess(true);
      setOpenPasswordDialog(false);
    } catch (error) {
      console.error('Error resetting password:', error);
      setPasswordResetError('Failed to send password reset email. Please try again.');
    }
  };

  const handlePasswordResetEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordResetEmail(e.target.value);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login');
  };

  const navigateTo = (path: string) => {
    console.log(`Navigating to: ${path}`);
    navigate(path);
    if (isSmallScreen) {
      handleDrawerClose();
    }
  };

  const LEAVE_MANAGER_VISIBLE_ROLES: Array<'USER' | 'MANAGER' | 'ADMIN'> = ['USER', 'MANAGER', 'ADMIN'];

  const groupLabels: Record<string, string> = {
    core: '',
    programmes: 'Programmes',
    tracking: 'Tracking',
    tools: 'Tools',
    management: 'Management',
    admin: 'Admin',
  };

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      roles: ['USER', 'MANAGER', 'ADMIN'],
      color: '#2563EB',
      group: 'core'
    },
    {
      text: 'Clients',
      icon: <GroupsIcon />,
      path: '/clients',
      roles: ['USER', 'MANAGER', 'ADMIN'],
      color: '#7C3AED',
      group: 'core'
    },
    {
      text: 'Ignition Overview',
      icon: <LocalFireDepartmentIcon />,
      path: '/ignition',
      roles: ['USER', 'MANAGER', 'ADMIN'],
      color: '#EF4444',
      group: 'programmes'
    },
    {
      text: 'Foundation Overview',
      icon: <FoundationIcon />,
      path: '/foundation',
      roles: ['USER', 'MANAGER', 'ADMIN'],
      color: '#3B82F6',
      group: 'programmes'
    },
    {
      text: 'Pre/Post Natal Overview',
      icon: <ChildCareIcon />,
      path: '/pre-post-natal',
      roles: ['USER', 'MANAGER', 'ADMIN'],
      color: '#0EA5E9',
      group: 'programmes'
    },
    {
      text: 'Success Overview',
      icon: <AssignmentIcon />,
      path: '/success',
      roles: ['MANAGER', 'ADMIN'],
      color: '#10B981',
      group: 'tracking'
    },
    {
      text: 'Success Nominations',
      icon: <EmojiEventsIcon />,
      path: '/success-nominations',
      roles: ['MANAGER', 'ADMIN'],
      color: '#F59E0B',
      group: 'tracking'
    },
    {
      text: 'Reports',
      icon: <AssessmentIcon />,
      path: '/reports',
      roles: ['USER', 'MANAGER', 'ADMIN'],
      color: '#F59E0B',
      group: 'tracking'
    },
    {
      text: 'Notices',
      icon: <CampaignIcon />,
      path: '/notices',
      roles: ['USER', 'MANAGER', 'ADMIN'],
      color: '#3B82F6',
      group: 'tools'
    },
    {
      text: 'Notepad & Tasks',
      icon: <NoteAltIcon />,
      path: '/notepad',
      roles: ['USER', 'MANAGER', 'ADMIN'],
      color: '#EC4899',
      group: 'tools'
    },
    // Staff Performance is open to every role (Oct 2026): coaches see their own
    // page, managers their location, admins everyone. Team Performance remains
    // ADMIN-only pending further dev work (it was ['MANAGER', 'ADMIN']); keep its
    // route guard in App.tsx in step.
    {
      text: 'Team Performance',
      icon: <GroupIcon />,
      path: '/team-performance',
      roles: ['ADMIN'],
      color: '#8B5CF6',
      group: 'management'
    },
    {
      text: 'Staff Performance',
      icon: <InsightsIcon />,
      path: '/staff-performance',
      roles: ['USER', 'MANAGER', 'ADMIN'],
      color: '#4F46E5',
      group: 'management'
    },
    {
      text: 'Guides',
      icon: <MenuBookIcon />,
      path: '/guides',
      roles: ['USER', 'MANAGER', 'ADMIN'],
      color: '#06B6D4',
      group: 'tools'
    },
    {
      text: 'Feedback',
      icon: <FeedbackIcon />,
      path: '/feedback',
      roles: ['USER', 'MANAGER', 'ADMIN'],
      color: '#84CC16',
      group: 'management'
    },
    {
      text: 'Leave Manager',
      icon: <CalendarMonthIcon />,
      path: '/leave-manager',
      roles: LEAVE_MANAGER_VISIBLE_ROLES,
      color: '#0F766E',
      group: 'management'
    },
    {
      text: 'Admin Panel',
      icon: <AdminPanelSettingsIcon />,
      path: '/admin',
      roles: ['ADMIN', 'MANAGER'],
      color: '#6B7280',
      group: 'admin'
    },
    {
      text: 'Drive',
      icon: <FolderIcon />,
      path: '/drive',
      roles: ['USER','MANAGER', 'ADMIN'],
      color: '#0EA5E9',
      group: 'tools'
    },
    {
      text: 'Lactate Testing',
      icon: <ScienceIcon />,
      path: '/lactate-threshold',
      roles: ['USER', 'MANAGER', 'ADMIN'],
      color: '#2563EB',
      group: 'tools',
      requiresFeature: 'lactate_testing'
    },
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#FAFBFC' }}>
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Toolbar sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, minWidth: 0 }}>
            <Tooltip title={open ? "Close menu" : "Open menu"}>
              <IconButton
                aria-label={open ? "close drawer" : "open drawer"}
                onClick={toggleDrawer}
                edge="start"
                sx={{ 
                  marginRight: 2,
                  color: 'white',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <motion.div
                  animate={{ rotate: open ? 180 : 0 }}
                  transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                >
                  {open ? <MenuOpenIcon /> : <MenuRoundedIcon />}
                </motion.div>
              </IconButton>
            </Tooltip>
            <ButtonBase
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                textAlign: 'left',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-1px)',
                }
              }}
              onClick={() => navigateTo('/dashboard')}
              aria-label="Go to dashboard"
            >
              <Box
                component="img"
                src="/logo192w.png"
                alt="UFIT Logo"
                sx={{
                  height: 36,
                  width: 36,
                  mr: 2,
                  objectFit: 'contain',
                  filter: 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(7500%) hue-rotate(142deg) brightness(104%) contrast(104%) drop-shadow(0 2px 8px rgba(255,255,255,0.3))',
                }}
              />
              <Typography 
                variant="h6" 
                component="div" 
                noWrap
                sx={{ 
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  letterSpacing: '-0.02em',
                  color: 'white',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  gap: 1
                }}
              >
                Client Management System
	              </Typography>
	            </ButtonBase>
	          </Box>
	          {user && (
	            <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
	              <ClientOmnibox />
	            </Box>
	          )}
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
              <Fade in={true}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    display: { xs: 'none', sm: 'block' },
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 500,
                    fontSize: '0.95rem'
                  }}
                >
                  Welcome back, {user.displayName?.split(' ')[0] || 'User'}
                </Typography>
              </Fade>
              <NotificationBell />
              <Tooltip title="Account settings" TransitionComponent={Grow}>
                <IconButton
                  size="large"
                  edge="end"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleProfileMenuOpen}
                  sx={{ 
                    p: 0.5,
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    }
                  }}
                >
                  <Avatar 
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      bgcolor: 'white',
                      color: '#1E293B',
                      fontWeight: 600,
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        transform: 'scale(1.05)',
                      }
                    }}
                    src={user.photoURL || undefined}
                  >
                    {user.displayName ? user.displayName.charAt(0).toUpperCase() : 'U'}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                TransitionComponent={Fade}
                sx={{
                  '& .MuiPaper-root': {
                    borderRadius: 3,
                    minWidth: 220,
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                    mt: 1.5,
                    overflow: 'visible',
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  }
                }}
              >
                <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {user.displayName || user.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user.email}
                  </Typography>
                </Box>
                <MenuItem 
                  onClick={handlePasswordResetClick}
                  sx={{ 
                    py: 1.5,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.main,
                      }
                    }
                  }}
                >
                  <ListItemIcon>
                    <LockResetIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Reset Password</ListItemText>
                </MenuItem>
                <MenuItem 
                  onClick={handleLogout}
                  sx={{ 
                    py: 1.5,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.error.main,
                      }
                    }
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      
      {/* Drawer for small screens - temporary variant */}
      {isSmallScreen && (
        <Drawer
          variant="temporary"
          open={open}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              backgroundColor: '#FFFFFF',
              borderRight: 'none',
              boxShadow: '4px 0 24px rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          <DrawerContent
            userRole={userRole}
            menuItems={menuItems}
            groupLabels={groupLabels}
            location={location}
            theme={theme}
            navigateTo={navigateTo}
            hoveredItem={hoveredItem}
            setHoveredItem={setHoveredItem}
            hasFeature={hasFeature}
          />
        </Drawer>
      )}

      {/* Drawer for large screens - persistent variant */}
      {!isSmallScreen && (
        <Drawer
          variant="persistent"
          open={open}
          sx={{
            display: { xs: 'none', md: 'block' },
            width: open ? drawerWidth : 0,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              backgroundColor: '#FFFFFF',
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
            },
          }}
        >
          <DrawerContent
            userRole={userRole}
            menuItems={menuItems}
            groupLabels={groupLabels}
            location={location}
            theme={theme}
            navigateTo={navigateTo}
            hoveredItem={hoveredItem}
            setHoveredItem={setHoveredItem}
            hasFeature={hasFeature}
          />
        </Drawer>
      )}

	      <Box
	        component="main"
	        sx={{
	          flexGrow: 1,
	          p: { xs: 2, sm: 3, md: 4 },
	          width: { xs: '100%', md: open ? `calc(100% - ${drawerWidth}px)` : '100%' },
	          marginLeft: { xs: 0, md: open ? `0px` : 0 },
	          transition: theme.transitions.create(['margin', 'width'], {
	            easing: theme.transitions.easing.sharp,
	            duration: theme.transitions.duration.enteringScreen,
	          }),
	        }}
	      >
	        <Toolbar />
	        <Container maxWidth="xl" sx={{ height: '100%' }}>
	          <Outlet />
	        </Container>
	      </Box>

      {/* Password Reset Dialog */}
      <Dialog 
        open={openPasswordDialog} 
        onClose={handlePasswordDialogClose}
        TransitionComponent={Fade}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Reset Password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Enter your email address and we'll send you a link to reset your password.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={passwordResetEmail}
            onChange={handlePasswordResetEmailChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  }
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handlePasswordDialogClose}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePasswordReset} 
            variant="contained" 
            sx={{ 
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
              }
            }}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Success Snackbar */}
	      <Snackbar 
	        open={passwordResetSuccess} 
	        autoHideDuration={6000} 
	        onClose={() => setPasswordResetSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Grow}
      >
        <Alert 
          onClose={() => setPasswordResetSuccess(false)} 
          severity="success" 
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
        >
          Password reset email sent! Check your inbox.
	        </Alert>
	      </Snackbar>

	      {/* Password Reset Error Snackbar */}
	      <Snackbar 
	        open={Boolean(passwordResetError)} 
	        autoHideDuration={6000} 
	        onClose={() => setPasswordResetError(null)}
	        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
	        TransitionComponent={Grow}
	      >
	        <Alert 
	          onClose={() => setPasswordResetError(null)} 
	          severity="error" 
	          sx={{ 
	            width: '100%',
	            borderRadius: 2,
	            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
	          }}
	        >
	          {passwordResetError}
	        </Alert>
	      </Snackbar>
    </Box>
  );
};

// Extracted drawer content into a separate component for reuse
interface DrawerContentProps {
  userRole: string | null;
  menuItems: Array<{
    text: string;
    icon: React.ReactNode;
    path: string;
    roles: string[];
    color?: string;
    group: string;
    requiresFeature?: string;
  }>;
  groupLabels: Record<string, string>;
  location: any;
  theme: any;
  navigateTo: (path: string) => void;
  hoveredItem: string | null;
  setHoveredItem: (item: string | null) => void;
  hasFeature: (feature: string) => boolean;
}

	const DrawerContent: React.FC<DrawerContentProps> = ({
	  userRole,
	  menuItems,
	  groupLabels,
	  location,
  theme,
  navigateTo,
  hoveredItem,
  setHoveredItem,
  hasFeature
}) => {
  // Filter menu items by role AND optional feature flag (pure helper).
  const accessibleItems = filterMenuItems(menuItems, userRole, hasFeature);

  return (
    <>
      <Toolbar />
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100% - 64px)',
        px: 1.5,
        pt: 2,
        pb: 2,
      }}>
        <List sx={{ width: '100%', px: 0 }}>
          {accessibleItems.map((item, index) => {
            const isActive = location.pathname === item.path;
            const isHovered = hoveredItem === item.text;
            const highlightColor = alpha(item.color || theme.palette.primary.main, 0.12);
            const isHighlighted = isActive || isHovered;
            const showGroupLabel = index === 0
              ? !!groupLabels[item.group]
              : item.group !== accessibleItems[index - 1]?.group;

            return (
              <React.Fragment key={item.text}>
                {/* Group spacing and label */}
                {showGroupLabel && (
                  <>
                    {index > 0 && <Box sx={{ mt: 1.5 }} />}
                    {groupLabels[item.group] && (
                      <Typography
                        variant="overline"
                        sx={{
                          px: 2,
                          pt: index === 0 ? 0 : 0.5,
                          pb: 0.5,
                          display: 'block',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          color: 'text.disabled',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {groupLabels[item.group]}
                      </Typography>
                    )}
                  </>
                )}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.05,
                    ease: [0.4, 0, 0.2, 1]
                  }}
                >
                  <ListItem
                    disablePadding
                    sx={{
                      mb: 0.25,
                      position: 'relative',
                    }}
                    onMouseEnter={() => setHoveredItem(item.text)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <motion.div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '4px',
                        height: '70%',
                        backgroundColor: item.color || theme.palette.primary.main,
                        borderRadius: '0 2px 2px 0',
                      }}
                      initial={{ width: 0 }}
                      animate={{ width: isActive || isHovered ? 4 : 0 }}
                      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    />
                    <ListItemButton
                      onClick={() => navigateTo(item.path)}
                      selected={isActive}
                      sx={{
                        borderRadius: 2.5,
                        py: 1,
                        px: 2,
                        mx: 0.5,
                        position: 'relative',
                        overflow: 'hidden',
                        alignItems: 'center',
                        minHeight: 44,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&.Mui-selected': {
                          backgroundColor: alpha(item.color || theme.palette.primary.main, 0.08),
                          '& .MuiListItemIcon-root': {
                            color: item.color || theme.palette.primary.main,
                          },
                          '& .MuiListItemText-primary': {
                            color: theme.palette.text.primary,
                            fontWeight: 600,
                          }
                        },
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.text.primary, 0.04),
                          '& .nav-indicator': {
                            opacity: 1,
                            transform: 'scale(1)',
                          }
                        }
                      }}
                      onFocus={() => setHoveredItem(item.text)}
                      onBlur={(event) => {
                        if (!event.currentTarget.contains(event.relatedTarget)) {
                          setHoveredItem(null);
                        }
                      }}
                    >
                      {isHighlighted && (
                        <motion.div
                          layoutId="nav-highlight"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: 20,
                            background: highlightColor,
                            boxShadow: '0 12px 30px rgba(31, 41, 55, 0.08)',
                            zIndex: -1,
                          }}
                          transition={{
                            type: 'spring',
                            stiffness: 380,
                            damping: 30,
                          }}
                        />
                      )}
                      <ListItemIcon sx={{
                        color: isActive
                          ? item.color || theme.palette.primary.main
                          : theme.palette.text.secondary,
                        minWidth: 40,
                        transition: 'all 0.2s',
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          backgroundColor: isActive ? alpha(item.color || theme.palette.primary.main, 0.1) : 'transparent',
                          transition: 'all 0.2s',
                        }}>
                          {item.icon}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontWeight: isActive ? 600 : 500,
                          fontSize: '0.875rem',
                          color: isActive
                            ? theme.palette.text.primary
                            : theme.palette.text.secondary,
                        }}
                      />
                      {isActive && (
                        <DotIcon
                          className="nav-indicator"
                          sx={{
                            fontSize: 8,
                            color: item.color || theme.palette.primary.main,
                            transition: 'all 0.3s',
                            opacity: isActive ? 1 : 0,
                            transform: isActive ? 'scale(1)' : 'scale(0)',
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                </motion.div>
              </React.Fragment>
            );
          })}
        </List>
        <Box sx={{ flexGrow: 1 }} />
      </Box>
    </>
  );
};

export default AppLayout;
